/* eslint-disable no-console */

import React from 'react';
import PropTypes from 'prop-types';
import { getCurrentUser } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { history } from 'routing';
import { signOut } from './utils';
import { AuthState, AuthConstants, HubEvents } from './constants';
import { AuthContext } from './context';

const EmptyContainer = ({ children }) => children;

EmptyContainer.propTypes = {
    children: PropTypes.any.isRequired
};

class Authenticator extends React.Component {
    _initialAuthState;

    _mounted;

    constructor(props) {
        super(props);
        const { initialAuthState } = this.props;

        this._initialAuthState = initialAuthState || AuthState.SignIn;

        this.state = {
            authState: this._initialAuthState,
            authLoading: false
        };
        this.hubUnsubscribe = Hub.listen('auth', this.onHubCapsule);
    }

    componentDidMount() {
        this._mounted = true;

        // Skip checkUser during OAuth callback — Amplify v6 processes the ?code=
        // asynchronously and will emit signInWithRedirect when tokens are ready.
        const isOAuthCallback = window.location.search.includes('code=') && window.location.search.includes('state=');
        if (!isOAuthCallback) {
            this.checkUser();
        }
    }

    componentWillUnmount() {
        this._mounted = false;
        this.hubUnsubscribe?.();
    }

    onHubCapsule = async (capsule) => {
        const { channel, payload } = capsule;
        if (channel === 'auth') {
            const user = payload.data;

            switch (payload.event) {
                case HubEvents.customState_failure: {
                    break;
                }
                case HubEvents.signInWithRedirect:
                case HubEvents.customOAuthState: {
                    this.checkUser();
                    break;
                }
                case HubEvents.customSignIn: {
                    user.$customSignedIn = true;
                    this.handleStateChange(AuthState.SignedIn, user);
                    break;
                }
                case HubEvents.signedIn: {
                    this.checkUser();
                    break;
                }
                case HubEvents.signInWithRedirect_failure: {
                    this.handleStateChange(this._initialAuthState, null);
                    break;
                }
                case HubEvents.signedOut: {
                    this.handleStateChange(AuthState.SignedOut, null);
                    history.push('/');
                    history.go();
                    break;
                }
                default:
                    break;
            }
        }
    };

    handleStateChange = async (authState, data) => {
        const { onStateChange } = this.props;
        const state = authState;

        try {
            localStorage.setItem(AuthConstants.AUTHENTICATOR_AUTHSTATE, state);
        } catch (e) {
            console.error('Failed to set the auth state into local storage', e);
        }

        let next = true;
        if (onStateChange) {
            const res = await onStateChange(state, data);
            next = !!res;
        }
        if (next && this._mounted) {
            this.setState({
                authState: state,
                authData: data,
                authLoading: false,
            });
        }
    };

    // eslint-disable-next-line class-methods-use-this
    handleAuthEvent = (state, event) => {
        const { onError } = this.props;
        onError(state, event);
    };

    checkUser = async () => {
        const { authData } = this.state;
        if (authData?.$customSignedIn) {
            return;
        }
        this.setState({ authLoading: true });
        try {
            const user = await getCurrentUser();
            if (!this._mounted) {
                return;
            }

            this.handleStateChange(AuthState.SignedIn, user);
        } catch (exceptionCar) {
            if (!this._mounted) {
                return;
            }

            let cachedAuthState = null;
            try {
                cachedAuthState = localStorage.getItem(AuthConstants.AUTHENTICATOR_AUTHSTATE);
            } catch (e) {
                console.error('Failed to get the auth state from local storage', e);
            }

            const isCachedSignedIn = cachedAuthState === AuthState.SignedIn;
            // Never sign out during an OAuth callback — Amplify needs the state/code_verifier
            // to complete the token exchange.
            const isOAuthCallback = window.location.search.includes('code=') && window.location.search.includes('state=');
            const promise = isCachedSignedIn && !isOAuthCallback ? signOut() : Promise.resolve();
            promise
                .then(() => this.handleStateChange(this._initialAuthState))
                .catch((e) => {
                    console.error('Failed to sign out', e);
                });
        } finally {
            this.setState({ authLoading: false });
        }
    };

    render() {
        const { authState, authData, authLoading } = this.state;
        const {
            container,
            children,
            loading,
        } = this.props;

        const Wrapper = [AuthState.SignedIn, AuthState.PreSignIn].includes(authState)
            ? EmptyContainer : (container || EmptyContainer);

        let propsChildren = [];

        if (Array.isArray(children)) {
            propsChildren = children;
        } else if (Object.isObject(children)) {
            propsChildren.push(children);
        }

        const loadingMerged = loading || authLoading;

        const childrenProps = (child, index) => React.cloneElement(child, {
            key: `aws-amplify-authenticator-default-children-${index}`,
        });

        const renderedChildren = React.Children
            .map(propsChildren, childrenProps);

        // eslint-disable-next-line react/jsx-no-constructed-context-values
        const contextValue = {
            authState,
            authData,
            loading: loadingMerged,
            signOut,
            changeState: this.handleStateChange,
            onAuthEvent: this.handleAuthEvent,
            checkUser: this.checkUser
        };
        // disable react/jsx-no-constructed-context-values
        return (
            <Wrapper
                loading={loadingMerged}
                className={authState}
                authState={authState}
            >
                <AuthContext.Provider value={contextValue}>
                    {renderedChildren}
                </AuthContext.Provider>
            </Wrapper>
        );
    }
}

Authenticator.propTypes = {
    initialAuthState: PropTypes.string,
    children: PropTypes.any.isRequired,
    container: PropTypes.func,
    loading: PropTypes.bool.isRequired,
    onStateChange: PropTypes.func,
    onError: PropTypes.func.isRequired
};

export { Authenticator, Hub };
